﻿import { FC } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, Button, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { getProductBySlug, formatPriceINR, ATS_RESUME_MASTER_KIT_SLUG } from '../config/catalog';
import { trackProductView, trackBuyClick, trackProductDownload } from '../analytics/events';
import { appConfig } from '../config/env';
import { PageProps } from './HomePage';

export const ThankYouPage: FC<PageProps> = ({ onNavigate }) => {
  const atsProduct = getProductBySlug(ATS_RESUME_MASTER_KIT_SLUG);
 
  const downloadToken =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('token')
      : null;

  const handleSecureDownload = () => {
    if (!downloadToken) return;

    trackProductDownload('free-interview-guide', downloadToken);

    window.location.href =
      `${appConfig.apiBaseUrl}/download?token=${encodeURIComponent(downloadToken)}`;
  };  

  const handleExploreATS = () => {
    if (atsProduct) {
      trackProductView(atsProduct.slug, atsProduct.priceINR, 'INR');
      trackBuyClick(atsProduct.slug, atsProduct.priceINR);
      onNavigate(atsProduct.routePath);
    }
  };

  return (
    <>
      <SEOHead
        title="Thank You — Free Guide Confirmed"
        description="Your download request has been received. Check your inbox for the Interview Preparation Starter Guide."
        noIndex={true}
      />

      <Section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '5rem 0' }}>
        <Container size="md">
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '4rem',
                height: '4rem',
                borderRadius: '50%',
                backgroundColor: '#ecfdf5',
                color: '#059669',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                fontWeight: 800,
                marginBottom: '1.25rem',
                border: '1px solid #a7f3d0'
              }}
            >
              ✓
            </div>

            <Badge variant="success" style={{ marginBottom: '1rem' }}>Request Received</Badge>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
              Your Free Guide Is On Its Way!
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1rem', lineHeight: 1.6 }}>
              Thank you for downloading the <strong>Interview Preparation Starter Guide</strong>. Please check your email inbox shortly for your direct download link.
            </p>
		
	    {downloadToken && (
              <div style={{ marginTop: '1.5rem' }}>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSecureDownload}
                >
                  Download Your Free Guide →
                </Button>
              </div>
            )} 	

            <div style={{ marginTop: '1.5rem', padding: '1.25rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-subtle)', textAlign: 'left' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.35rem' }}>
                Next Steps:
              </div>
              <ul style={{ paddingLeft: '1.25rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <li>Check your inbox (and spam/promotions folder just in case) for an email from Connect Source.</li>
                <li>Review the 10-point checklist before your upcoming interviews.</li>
                <li>Save the STAR framework template for drafting your behavioral answers.</li>
              </ul>
            </div>
          </div>

          {/* Optional Recommended Next Step: ATS Resume Master Kit */}
          {atsProduct && (
            <div style={{ marginTop: '3.5rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
                <Badge variant="primary">Recommended Next Step (Optional)</Badge>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-navy-dark)', marginTop: '0.5rem' }}>
                  Boost Your Interview Callbacks with an ATS-Optimized Resume
                </h2>
                <p style={{ fontSize: '0.925rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                  The best interview preparation only works if you get invited into the room.
                </p>
              </div>

              <Card style={{ padding: '2rem', border: '2px solid #bae6fd', backgroundColor: '#f0f9ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <Badge variant="neutral">{atsProduct.deliverableType.replace('_', ' ')}</Badge>
                    <CardTitle style={{ fontSize: '1.35rem', marginTop: '0.5rem' }}>{atsProduct.title}</CardTitle>
                    <CardDescription style={{ marginTop: '0.35rem', maxWidth: '500px', lineHeight: 1.55 }}>
                      {atsProduct.subtitle}
                    </CardDescription>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                      {formatPriceINR(atsProduct.priceINR)}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                    Includes 5 Word & Google Docs templates + Action Verb Bank
                  </div>
                  <Button variant="primary" size="md" onClick={handleExploreATS}>
                    Explore ATS Master Kit ({formatPriceINR(atsProduct.priceINR)}) →
                  </Button>
                </div>
              </Card>

              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <Button variant="ghost" size="sm" onClick={() => onNavigate(ROUTES.HOME)}>
                  Return to Home
                </Button>
              </div>
            </div>
          )}
        </Container>
      </Section>
    </>
  );
};



